const WORKER_PROFILE_PARENT_SECTION_TITLES: Readonly<{
  readonly workerDetails: string;
  readonly workerSkillsAndPay: string;
  readonly workerEmploymentRelationship: string;
  readonly workyTrustVault: string;
  readonly customActions: string;
}> = {
  workerDetails: 'Worker Details',
  workerSkillsAndPay: 'Worker Skills and Pay',
  workerEmploymentRelationship: 'Worker Employment Relationship',
  workyTrustVault: 'Worky TrustVault',
  customActions: 'Custom Actions',
};

const CLIENT_FACING_WORKER_PROFILE_SECTION_TOOLTIPS: Readonly<{
  readonly workerDetails: string;
  readonly email: string;
  readonly phone: string;
  readonly socialInsuranceNumber: string;
  readonly dateOfBirth: string;
  readonly homeAddress: string;
  readonly workerSkillsAndPay: string;
  readonly attributes: string;
  readonly workerTypes: string;
  readonly workerEmploymentRelationship: string;
  readonly contractorDetails: string;
  readonly employmentRelationshipConfigs: string;
  readonly workyTrustVault: string;
  readonly checklist: string;
  readonly workerFiles: string;
  readonly workerTasks: string;
  readonly customActions: string;
  readonly preTrainedFacility: string;
  readonly profileNotes: string;
  readonly workerStatus: string;
  readonly fullyHiredStatus: string;
  readonly integrationConfigurations: string;
  readonly quickBooksHoursSync: string;
  readonly workerDisplayName: string;
  readonly editWorkerName: string;
  readonly revealSocialInsuranceNumber: string;
  readonly hideSocialInsuranceNumber: string;
  readonly editSocialInsuranceNumber: string;
  readonly editDateOfBirth: string;
  readonly editHomeAddress: string;
  readonly addWorkerAttribute: string;
  readonly workerAttributeName: string;
  readonly workerAttributeValue: string;
  readonly workerAttributeActions: string;
  readonly editWorkerAttribute: string;
  readonly removeWorkerAttribute: string;
  readonly workerAttributeRecordedByAdmin: string;
  readonly workerAttributeRecordedFromWorkTask: string;
  readonly addWorkerType: string;
  readonly workerTypePayRate: string;
  readonly editWorkerPayRate: string;
  readonly saveWorkerPayRate: string;
  readonly cancelWorkerPayRate: string;
  readonly removeWorkerType: string;
  readonly createContractorDetails: string;
  readonly contractorBusinessName: string;
  readonly contractorBusinessNumber: string;
  readonly contractorTaxNumber: string;
  readonly contractorBusinessAddress: string;
  readonly contractorInvoicingNotes: string;
  readonly addEmploymentRelationship: string;
  readonly payStubNote: string;
  readonly savePayStubNote: string;
  readonly uploadWorkerFile: string;
  readonly addPreTrainedFacility: string;
}> = {
  workerDetails:
    'Email, phone, social insurance number, date of birth, and home address for this worker.',
  email:
    'The worker’s primary email address. Select the address to copy it.',
  phone:
    'The worker’s primary phone number. Select the number to copy it.',
  socialInsuranceNumber:
    'The social insurance number on file for this worker. It stays hidden until you choose Reveal.',
  dateOfBirth:
    'The date of birth on file for this worker. Use Edit to change it.',
  homeAddress:
    'The home address on file for this worker. Search and choose an address before saving a change.',
  workerSkillsAndPay:
    'The worker’s qualifications, assigned roles, and default hourly pay rates.',
  attributes:
    'Skills, certifications, and other qualifications recorded for this worker.',
  workerTypes:
    'The roles this worker can fill and the default hourly pay rate for each role.',
  workerEmploymentRelationship:
    'Business and payroll settings that describe how this worker is engaged and paid.',
  contractorDetails:
    'Business information used when this worker provides services as a contractor.',
  employmentRelationshipConfigs:
    'The payroll relationship and jurisdiction settings used to calculate this worker’s pay.',
  workyTrustVault:
    'Onboarding checklists, worker documents, and assigned work tasks managed for this worker.',
  checklist:
    'The onboarding steps this worker must complete before they are ready for work.',
  workerFiles:
    'Secure worker documents, including uploaded files, expiry dates, and review status.',
  workerTasks:
    'Forms and tasks assigned to this worker, including completion and review status.',
  customActions:
    'Additional worker setup actions that do not change the worker’s core profile.',
  preTrainedFacility:
    'Mark this worker as already trained for a specific client facility so they can be assigned there.',
  profileNotes:
    'Internal notes about this worker for your agency team.',
  workerStatus:
    'Workers can only receive shifts when they are Fully Hired.',
  fullyHiredStatus:
    'Choose Fully Hired so this worker can be assigned to shifts.',
  integrationConfigurations:
    'Choose which connected systems receive this worker’s hours.',
  quickBooksHoursSync:
    'When this is off, this worker’s hours are not sent to QuickBooks.',
  workerDisplayName:
    'The name shown for this worker on schedules and pay records.',
  editWorkerName:
    'Change the first or last name shown for this worker.',
  revealSocialInsuranceNumber:
    'Show the social insurance number on this screen. Choose Hide to cover it again.',
  hideSocialInsuranceNumber:
    'Cover the social insurance number on this screen.',
  editSocialInsuranceNumber:
    'Update the social insurance number on file. Use 9 digits.',
  editDateOfBirth:
    'Update the date of birth on file for this worker.',
  editHomeAddress:
    'Search for the home address and choose it before you save.',
  addWorkerAttribute:
    'Record a skill, certification, or other qualification for this worker.',
  workerAttributeName:
    'The qualification recorded for this worker.',
  workerAttributeValue:
    'The answer or level recorded for this qualification.',
  workerAttributeActions:
    'Change or remove this qualification.',
  editWorkerAttribute:
    'Change the recorded answer for this qualification.',
  removeWorkerAttribute:
    'Remove this qualification from the worker.',
  workerAttributeRecordedByAdmin:
    'Someone on your agency team recorded this qualification.',
  workerAttributeRecordedFromWorkTask:
    'This qualification was recorded from a completed work task.',
  addWorkerType:
    'Add a role this worker can fill and set the default hourly pay for that role.',
  workerTypePayRate:
    'The default hourly pay used when this worker is assigned to this role.',
  editWorkerPayRate:
    'Change the default hourly pay for this role.',
  saveWorkerPayRate:
    'Save the new hourly pay for this role.',
  cancelWorkerPayRate:
    'Keep the current hourly pay.',
  removeWorkerType:
    'Remove this role so it is no longer offered when you assign this worker.',
  createContractorDetails:
    'Add the business information used when this worker is paid as a contractor.',
  contractorBusinessName:
    'The business name used when this worker invoices as a contractor.',
  contractorBusinessNumber:
    'The business number used on this contractor’s invoices.',
  contractorTaxNumber:
    'The tax number used on this contractor’s invoices.',
  contractorBusinessAddress:
    'The business address used on this contractor’s invoices.',
  contractorInvoicingNotes:
    'A note included when this contractor is invoiced.',
  addEmploymentRelationship:
    'Add the payroll relationship used to calculate this worker’s pay.',
  payStubNote:
    'A note that appears on this worker’s pay stub for this payroll relationship.',
  savePayStubNote:
    'Save the pay stub note for this payroll relationship.',
  uploadWorkerFile:
    'Upload a document for this worker, such as a certificate or identification.',
  addPreTrainedFacility:
    'Choose a client facility where this worker is already trained.',
};

const WORKER_PROFILE_INTEGRATION_CONFIGURATION_LABELS: Readonly<{
  readonly dialogTitle: string;
  readonly quickBooksHoursSync: string;
  readonly save: string;
  readonly cancel: string;
  readonly saveSuccess: string;
  readonly saveFailed: string;
}> = {
  dialogTitle: 'Integration configurations',
  quickBooksHoursSync: 'QuickBooks hours sync',
  save: 'Save',
  cancel: 'Cancel',
  saveSuccess: 'QuickBooks hours sync saved',
  saveFailed: 'Unable to save QuickBooks hours sync',
};

export {
  WORKER_PROFILE_PARENT_SECTION_TITLES,
  CLIENT_FACING_WORKER_PROFILE_SECTION_TOOLTIPS,
  WORKER_PROFILE_INTEGRATION_CONFIGURATION_LABELS,
};
